import type { Bi } from 'schools/yantraVidya/types/bilingual';

export type YantraTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type YantraChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: YantraTopic[];
};

export const YANTRA_HERO = {
  eyebrow: { en: 'Sacred geometry · Form', hi: 'पवित्र ज्यामिति · रूप' },
  titleShort: { en: 'Yantra Vidya', hi: 'यंत्र विद्या' },
  title: { en: 'Yantra Vidya', hi: 'यंत्र विद्या' },
  motto: {
    en: 'Learn how sacred diagrams are built from bindu, triangles, lotus, and gates - drawn step by step.',
    hi: 'जानें पवित्र रेखाचित्र बिन्दु, त्रिभुज, कमल और द्वार से कैसे बनते हैं - क्रम से खींचे जाते हैं।',
  },
  tagline: {
    en: 'Twenty-five lessons with a construction diagram on every page - types, bindu, triangles, lotus, gates, and how to read layers.',
    hi: 'पच्चीस पाठ - हर पृष्ठ पर रचना चित्र: प्रकार, बिन्दु, त्रिभुज, कमल, द्वार और स्तर पढ़ना।',
  },
  intro: {
    en: 'Pair with Vastu for spatial mandala and Panchang for timing - this school teaches yantra geometry in plain language.',
    hi: 'स्थान मंडल हेतु वास्तु और समय हेतु पंचांग के साथ - यह स्कूल सरल भाषा में यंत्र ज्यामिति सिखाता है।',
  },
  trait: {
    en: 'Bindu + triangle + lotus + gate = readable yantra',
    hi: 'बिन्दु + त्रिभुज + कमल + द्वार = पढ़ने योग्य यंत्र',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): YantraTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const YANTRA_CHAPTERS: YantraChapter[] = [
  {
    id: 'start-here',
    number: 1,
    title: { en: 'Start here', hi: 'शुरुआत' },
    subtitle: { en: 'What yantras are and why geometry matters', hi: 'यंत्र क्या हैं और ज्यामिति क्यों महत्वपूर्ण' },
    icon: '✦',
    topics: [
      topic('what-is-yantra', 'What Is a Yantra?', 'यंत्र क्या है?', 'A yantra is a precise sacred diagram - not decoration.', 'यंत्र एक सटीक पवित्र रेखाचित्र है - सजावट नहीं।', 10, '✦'),
      topic(
        'types-of-yantras',
        'Types of Yantras',
        'यंत्र के प्रकार',
        'Six families - deity, graha, vastu, protection, health, and meditation.',
        'छह परिवार - देवता, ग्रह, वास्तु, रक्षा, स्वास्थ्य और ध्यान।',
        14,
        '🗂️',
      ),
      topic('yantra-mandala-tantra', 'Yantra vs Mandala vs Tantra', 'यंत्र बनाम मंडल बनाम तंत्र', 'Three related but distinct tools.', 'तीन संबंधित पर अलग उपकरण।', 10, '◎'),
      topic('mantra-yantra-tantra-triad', 'Mantra–Yantra–Tantra', 'मंत्र–यंत्र–तंत्र', 'Sound, form, and method work together.', 'ध्वनि, रूप और विधि साथ काम करते हैं।', 11, '🔺'),
      topic('sacred-geometry-primitives', 'Point, Line, Circle', 'बिन्दु, रेखा, वृत्त', 'Every yantra grows from three primitives.', 'हर यंत्र तीन मूल तत्वों से बढ़ता है।', 11, '○'),
      topic('why-geometry-matters', 'Why Proportions Matter', 'अनुपात क्यों महत्वपूर्ण', 'Wrong ratios break traditional meaning.', 'गलत अनुपात पारंपरिक अर्थ तोड़ते हैं।', 10, '⚖️'),
      topic('ethics-and-guru-lineage', 'Ethics & Guru Lineage', 'नैतिकता और गुरु परंपरा', 'Study with respect - not commercial misuse.', 'सम्मान से पढ़ें - व्यावसायिक दुरुपयोग नहीं।', 12, '🙏'),
    ],
  },
  {
    id: 'bindu-frame',
    number: 2,
    title: { en: 'Bindu & frame', hi: 'बिन्दु और फ्रेम' },
    subtitle: { en: 'Central point, axes, circle, and square', hi: 'केंद्र बिंदु, अक्ष, वृत्त और वर्ग' },
    icon: '•',
    topics: [
      topic('the-bindu', 'The Bindu', 'बिन्दु', 'The central point of manifestation.', 'अभिव्यक्ति का केंद्रीय बिंदु।', 10, '•'),
      topic('shiva-axis', 'Vertical Axis (Shiva)', 'ऊर्ध्व अक्ष (शिव)', 'The vertical line through bindu.', 'बिन्दु से होकर गुजरने वाली ऊर्ध्व रेखा।', 10, '│'),
      topic('shakti-axis', 'Horizontal Axis (Shakti)', 'क्षैतिज अक्ष (शक्ति)', 'The cross completes the field.', 'क्रॉस क्षेत्र पूर्ण करता है।', 10, '╋'),
      topic('circumscribing-circle', 'Circumscribing Circle', 'परिवृत्त वृत्त', 'Circle drawn around the bindu.', 'बिन्दु के चारों ओर खींचा वृत्त।', 11, '○'),
      topic('circle-in-square', 'Circle in Square', 'वर्ग में वृत्त', 'Earth-square frames the cosmos.', 'भूपुर ब्रह्मांड को घेरता है।', 11, '□'),
      topic('orientation-east-gate', 'East-Facing Orientation', 'पूर्वमुख अभिविन्यास', 'Many yantras open toward the east.', 'कई यंत्र पूर्व की ओर खुलते हैं।', 10, '🧭'),
    ],
  },
  {
    id: 'triangles',
    number: 3,
    title: { en: 'Triangles & Shatkona', hi: 'त्रिभुज और षट्कोण' },
    subtitle: { en: 'Upward, downward, and six-point star', hi: 'ऊर्ध्व, अधो और षट्कोण' },
    icon: '△',
    topics: [
      topic('upward-triangle', 'Upward Triangle', 'ऊर्ध्व त्रिभुज', 'Shiva triangle - fire ascending.', 'शिव त्रिभुज - ऊर्ध्वाग्नि।', 11, '△'),
      topic('downward-triangle', 'Downward Triangle', 'अधो त्रिभुज', 'Shakti triangle - flow descending.', 'शक्ति त्रिभुज - अधोगामी प्रवाह।', 11, '▽'),
      topic('shatkona', 'Shatkona', 'षट्कोण', 'Intersection of Shiva and Shakti.', 'शिव और शक्ति का मिलन।', 12, '✡'),
      topic('triangle-names', 'Triangle Names', 'त्रिभुज नाम', 'Learn labels after construction.', 'रचना के बाद नाम सीखें।', 10, '🏷️'),
      topic('equilateral-rules', 'Equilateral Rules', 'समबाहु नियम', 'Construction arcs guide proportion.', 'रचना चाप अनुपात मार्गदर्शन करते हैं।', 11, '📐'),
      topic('triangles-in-yantras', 'Triangles in Yantras', 'यंत्रों में त्रिभुज', 'Full frame: bindu, cross, both triangles.', 'पूर्ण फ्रेम: बिन्दु, क्रॉस, दोनों त्रिभुज।', 12, '🔺'),
    ],
  },
  {
    id: 'lotus-gates',
    number: 4,
    title: { en: 'Lotus, gates & first yantras', hi: 'कमल, द्वार और पहले यंत्र' },
    subtitle: { en: 'Petals, bhupura, and reading layers', hi: 'पंखुड़ियाँ, भूपुर और स्तर पढ़ना' },
    icon: '🪷',
    topics: [
      topic('lotus-four-petals', 'Four-Petal Lotus', 'चार पंखुड़ी कमल', 'Simple lotus ring around bindu.', 'बिन्दु के चारों ओर सरल कमल वलय।', 10, '🪷'),
      topic('lotus-eight-petals', 'Eight-Petal Lotus', 'आठ पंखुड़ी कमल', 'Classic eight-petal enclosure.', 'क्लासिक आठ पंखुड़ी आवरण।', 11, '🌸'),
      topic('bhupura-earth-square', 'Bhupura (Earth Square)', 'भूपुर (भू वर्ग)', 'Outer earth-square with corner marks.', 'कोने चिह्नों सहित बाह्य भू वर्ग।', 11, '⬜'),
      topic('four-gates-dvara', 'Four Gates (Dvāra)', 'चार द्वार', 'Gate breaks in the earth-square.', 'भू वर्ग में द्वार विभाजन।', 11, '🚪'),
      topic('ganesha-yantra-build', 'Build a Ganesha Yantra', 'गणेश यंत्र बनाएँ', 'Watch a simple yantra assembled.', 'एक सरल यंत्र की रचना देखें।', 14, '🐘'),
      topic('how-to-read-layers', 'How to Read Layers', 'स्तर कैसे पढ़ें', 'Outside-in: peel layers to bindu.', 'बाहर से अंदर: बिन्दु तक स्तर उतारें।', 12, '📖'),
    ],
  },
];

export const YANTRA_TOPIC_IDS = YANTRA_CHAPTERS.flatMap((c) => c.topics.map((t) => t.id));

export function getYantraTopic(topicId: string): { topic: YantraTopic; chapter: YantraChapter } | null {
  for (const chapter of YANTRA_CHAPTERS) {
    const t = chapter.topics.find((x) => x.id === topicId);
    if (t) return { topic: t, chapter };
  }
  return null;
}

export function getYantraChapter(chapterId: string): YantraChapter | undefined {
  return YANTRA_CHAPTERS.find((c) => c.id === chapterId);
}

export function getYantraChapterIndex(chapterId: string): number {
  return YANTRA_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getYantraTopicCount(): number {
  return YANTRA_CHAPTERS.reduce((n, c) => n + c.topics.length, 0);
}

export function getYantraTopicNeighbors(topicId: string): {
  prev: YantraTopic | null;
  next: YantraTopic | null;
} {
  const flat = YANTRA_CHAPTERS.flatMap((c) => c.topics);
  const idx = flat.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return { prev: flat[idx - 1] ?? null, next: flat[idx + 1] ?? null };
}
